/**
 * Turn LaTeX math in an agent's answer into plain text before it renders as markdown.
 *
 * The chat has no math renderer, so `$2,500 \text{ miles} \times \$0.20$` would show
 * its commands raw. Only text with a sign of LaTeX changes: a plain `$500` stays as
 * typed, and code blocks and code spans are left alone.
 */

const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/
const INLINE_CODE_RE = /(`+)[\s\S]*?\1/g
// $$display$$, \[display\], \(inline\), then $inline$ on one line with \$ escapes allowed.
const MATH_SPAN_RE = /\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|\$((?:\\.|[^$\\\n])+)\$/g
// A command or a ^{...} or _{...} group; a plain dollar amount has neither.
const MATH_SIGN_RE = /\\[a-zA-Z]+|[\^_]\{/
const WRAPPER_RE = /\\(text|textrm|mathrm|operatorname|mathbf|textbf|boldsymbol|boxed)\s*\{([^{}]*)\}/g
const BOLD_WRAPPERS = new Set(['mathbf', 'textbf', 'boldsymbol', 'boxed'])
const SQUARE_ROOT_RE = /\\sqrt\s*\{([^{}]*)\}/g
const FRACTION_RE = /\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g
const SYMBOL_RE = /\\([a-zA-Z]+)(?![a-zA-Z])/g
const SYMBOLS: Record<string, string> = {
  times: '×',
  cdot: '·',
  div: '÷',
  approx: '≈',
  le: '≤',
  leq: '≤',
  ge: '≥',
  geq: '≥',
  ne: '≠',
  neq: '≠',
  pm: '±',
  to: '→',
  rightarrow: '→',
  quad: ' ',
  qquad: ' ',
  left: '',
  right: '',
}
const EXPONENT_RE = /([\^_])\{([^{}]*)\}/g
// A brace group not attached to a command, such as {,} in 125{,}000.
const GROUP_RE = /(?<![a-zA-Z}])\{([^{}]*)\}/g
const SPACING_RE = /\\[,;: ]/g
const ESCAPED_CHARACTER_RE = /\\([$%&#])/g

export function plainTextMath(text: string): string {
  if (!MATH_SIGN_RE.test(text) && !text.includes('$$')) return text
  const lines = text.split('\n')
  const output: string[] = []
  let prose: string[] = []
  let fence: string | null = null
  const flushProse = () => {
    if (prose.length > 0) output.push(convertProse(prose.join('\n')))
    prose = []
  }
  for (const line of lines) {
    const marker = FENCE_RE.exec(line)?.[1]
    if (fence === null && marker) {
      flushProse()
      fence = marker
      output.push(line)
    } else if (fence !== null) {
      if (marker && marker[0] === fence[0] && marker.length >= fence.length) fence = null
      output.push(line)
    } else {
      prose.push(line)
    }
  }
  flushProse()
  return output.join('\n')
}

function convertProse(prose: string): string {
  let converted = ''
  let cursor = 0
  for (const code of prose.matchAll(INLINE_CODE_RE)) {
    converted += convertMath(prose.slice(cursor, code.index)) + code[0]
    cursor = code.index + code[0].length
  }
  return converted + convertMath(prose.slice(cursor))
}

/** Rewrite each $$ span and each other span that looks like math, then any command typed without delimiters. */
function convertMath(prose: string): string {
  let converted = ''
  let cursor = 0
  const spans = new RegExp(MATH_SPAN_RE.source, 'g')
  let span: RegExpExecArray | null
  while ((span = spans.exec(prose)) !== null) {
    const math = span.slice(1).find(Boolean) ?? ''
    if (!span[0].startsWith('$$') && !MATH_SIGN_RE.test(math)) {
      // Likely a dollar amount; its closing $ may open the real span.
      spans.lastIndex = span.index + 1
      continue
    }
    converted += prose.slice(cursor, span.index) + plainMath(math)
    cursor = spans.lastIndex
  }
  return replaceCommands(converted + prose.slice(cursor))
}

function plainMath(math: string): string {
  let plain = math
  let previous: string
  do {
    previous = plain
    plain = replaceCommands(
      plain
        .replace(EXPONENT_RE, (_, sign: string, exponent: string) => `${sign}${groupedPart(exponent)}`)
        .replace(GROUP_RE, '$1')
    )
  } while (plain !== previous)
  return plain
    .replace(SPACING_RE, ' ')
    .replace(ESCAPED_CHARACTER_RE, '$1')
    .replace(/[{}]/g, '')
    .replace(/ {2,}/g, ' ')
    .trim()
}

function replaceCommands(text: string): string {
  let replaced = text
  let previous: string
  // Innermost first, so \mathbf{\text{x}} and \frac{\text{a}}{b} unwrap fully.
  do {
    previous = replaced
    replaced = replaced
      .replace(
        FRACTION_RE,
        (fraction: string, numerator: string, denominator: string, at: number, whole: string) =>
          placedFraction(
            `${groupedPart(numerator)}/${groupedPart(denominator)}`,
            whole.slice(0, at),
            whole.slice(at + fraction.length)
          )
      )
      .replace(SQUARE_ROOT_RE, (_, radicand: string) => `√${groupedPart(radicand)}`)
      .replace(WRAPPER_RE, (_, command: string, inner: string) =>
        BOLD_WRAPPERS.has(command) ? `**${inner.trim()}**` : inner
      )
  } while (replaced !== previous)
  return replaced.replace(SYMBOL_RE, (command: string, name: string) => SYMBOLS[name] ?? command)
}

/** Keep a rewritten fraction apart from its neighbours: 2\frac{100}{4} is 2 × 100/4, and \frac{2}{3}^2 is (2/3)^2. */
function placedFraction(fraction: string, before: string, after: string): string {
  const grouped = /[/÷]\s*$/.test(before) || /^\s*[\^_]/.test(after) ? `(${fraction})` : fraction
  // A factor right before it, but not an operator such as \times or \cdot.
  return /[\w)]$/.test(before) && !/\\[a-zA-Z]+$/.test(before) ? ` × ${grouped}` : grouped
}

/** Parenthesize a fraction part, root or exponent made of more than one term, so 1/(2/3) keeps its meaning. */
function groupedPart(part: string): string {
  const trimmed = part.trim()
  return /[\s+\-/×·÷*^]|\\[a-zA-Z]/.test(trimmed) ? `(${trimmed})` : trimmed
}
